export const filterPackages = (allPackages, tripDetails) => {
    if (!allPackages || allPackages.length === 0) {
        return [];
    }

    // Prepare filters
    const days = tripDetails.duration ? parseInt(tripDetails.duration) : null;
    const keywords = tripDetails.keywords;
    const location = tripDetails.location;

    // Normalize keywords to an array of cleaned lowercase strings
    const keywordList = Array.isArray(keywords)
        ? keywords
        : (typeof keywords === "string" ? keywords.split(",") : []);

    const normalizedKeywords = keywordList
        .map(k => String(k).trim().toLowerCase())
        .filter(Boolean);

    // Normalize location
    const normalizedLocation =
        typeof location === "string" ? location.trim().toLowerCase() : "";

    // Filter packages
    const filtered = allPackages.filter(pkg => {
        if (!pkg || typeof pkg !== "object") return false;

        // --- Duration match ---
        if (typeof days === "number" && Number.isFinite(days)) {
            if (Number(pkg.duration) !== days) return false;
        }

        // --- Location match ---
        if (normalizedLocation) {
            const pkgLocation = String(pkg.location || "").trim().toLowerCase();
            if (pkgLocation !== normalizedLocation) return false;
        }

        // --- Keyword-in-tags match ---
        if (normalizedKeywords.length > 0) {
            const tags = Array.isArray(pkg.tags) ? pkg.tags : [];
            const normalizedTags = tags
                .map(t => {
                    // Handle both string tags and object tags with tagValue
                    if (typeof t === 'string') return t.trim().toLowerCase();
                    if (typeof t === 'object' && t.tagValue) return String(t.tagValue).trim().toLowerCase();
                    if (typeof t === 'object' && t.tagName) return String(t.tagName).trim().toLowerCase();
                    return '';
                })
                .filter(Boolean);

            // Match if any keyword is included in any tag (partial allowed)
            const hasKeyword = normalizedKeywords.some(kw =>
                normalizedTags.some(tag => tag.includes(kw))
            );

            if (!hasKeyword) return false;
        }

        return true;
    });

    return filtered;
};

export const htmlToPdfText = (html) => {
    if (!html) return '';

    // Create a DOM element to parse HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    let text = '';

    // We'll track ordered list counters by depth
    const olCounters = {};

    const traverse = (node, depth = 0) => {
        if (node.nodeType === Node.TEXT_NODE) {
            text += node.textContent;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            const tag = node.tagName.toLowerCase();

            // Handle ReactQuill's specific classes for indentation and lists
            let currentDepth = depth;
            const indentMatch = node.className && typeof node.className === 'string' ? node.className.match(/ql-indent-(\d+)/) : null;
            if (indentMatch) {
                currentDepth = parseInt(indentMatch[1], 10);
            }

            if (tag === 'br') {
                text += '\n';
            } else if (tag === 'p' || tag === 'div') {
                if (currentDepth > 0 && (text.length === 0 || text.endsWith('\n'))) {
                    text += '  '.repeat(currentDepth);
                }
                node.childNodes.forEach(child => traverse(child, currentDepth));
                text += '\n';
            } else if (tag === 'h1' || tag === 'h2' || tag === 'h3' || tag === 'h4' || tag === 'h5' || tag === 'h6') {
                node.childNodes.forEach(child => traverse(child, currentDepth));
                text += '\n\n';
            } else if (tag === 'ul' || tag === 'ol') {
                if (text.length > 0 && !text.endsWith('\n')) {
                    text += '\n';
                }
                // Reset counter for this depth
                olCounters[currentDepth] = 1;
                node.childNodes.forEach(child => traverse(child, currentDepth));
                if (!text.endsWith('\n')) text += '\n';
            } else if (tag === 'li') {
                const indentSpace = '  '.repeat(currentDepth);

                // Determine if it's an ordered list item
                const isOrdered = node.getAttribute('data-list') === 'ordered' || (node.parentNode && node.parentNode.tagName === 'OL');

                if (isOrdered) {
                    const count = olCounters[currentDepth] || 1;
                    // For indent 0: numbers, indent 1: letters (a, b, c)
                    const prefix = currentDepth === 0 ? `${count}.` : `${String.fromCharCode(96 + count)}.`;
                    text += `${indentSpace}${prefix} `;
                    olCounters[currentDepth] = count + 1;
                } else {
                    // Use standard unicode bullet (U+2022) supported by jsPDF
                    text += `${indentSpace}• `;
                }

                node.childNodes.forEach(child => traverse(child, currentDepth));
                if (!text.endsWith('\n')) text += '\n';
            } else {
                // Normal tags (strong, em, span, etc)
                node.childNodes.forEach(child => traverse(child, currentDepth));
            }
        }
    };

    traverse(doc.body, 0);

    // Clean up empty bullets and excessive newlines
    text = text.replace(/^[ \t]*•[ \t]*\n/gm, '');
    text = text.replace(/^[ \t]*\d+\.[ \t]*\n/gm, '');
    text = text.replace(/^[ \t]*[a-z]\.[ \t]*\n/gm, '');
    text = text.replace(/\n{3,}/g, '\n\n');

    return text.trim();
};

export const calculatePackageCost = (packageDetails, tripDetails) => {
    if (!packageDetails?.cost?.valueCost?.[0]?.price) {
        return 0;
    }

    const basePrice = packageDetails.cost.valueCost[0].price;
    const pax = (parseInt(tripDetails.pax) || 0) + (parseInt(tripDetails.kids_above_5) || 0);
    return basePrice * (pax || 1);
};

export const totalCost = (hotelCost, carCost, margin, optionalExtrasCost = 0) => {
    const baseSubtotal = (parseFloat(hotelCost) || 0) + (parseFloat(carCost) || 0);
    const marginAmount = baseSubtotal * (parseFloat(margin) / 100 || 0);
    return Math.round(baseSubtotal + marginAmount + parseFloat(optionalExtrasCost || 0));
};

export const totalCostSandakphu = (hotelCost, carCost, discount, tripDetails, selectedPackage) => {

    const hasLandRover = selectedPackage?.label?.includes("Land Rover");
    const head_count = parseInt(tripDetails.pax) + parseInt(tripDetails.kids_above_5) || 0;
    if (hasLandRover) {
        const totalCost = ((parseFloat(hotelCost) || 0) * head_count) + (parseFloat(carCost) || 0);
        const marginAmount = totalCost * (parseFloat(discount) / 100 || 0);
        return (totalCost - marginAmount) * tripDetails.duration;
    } else {
        const singleCost = selectedPackage?.details?.cost?.singleCost;
        const totalCost = singleCost * head_count;
        const marginAmount = totalCost * (parseFloat(discount) / 100 || 0);
        return (totalCost - marginAmount);
    }
};

export const updateItineraryByDuration = (currentItinerary, duration) => {
    const days = (parseInt(duration) || 0) + 1;

    if (currentItinerary.length === days) {
        return currentItinerary;
    }

    if (days > currentItinerary.length) {
        return [...currentItinerary, ...new Array(days - currentItinerary.length).fill('')];
    }

    return currentItinerary.slice(0, days);
};

export const prePopulateHotelSelections = (shortItinerary, allHotels) => {
    if (!shortItinerary || !Array.isArray(shortItinerary)) {
        return {};
    }

    const newHotelSelections = {};

    shortItinerary.forEach((item, index) => {
        if (item.tagValue) {
            // Extract destination from tagValue (e.g., "Gorkhey to Srikhola" -> "Srikhola")
            const parts = item.tagValue.split(' to ');
            const destination = parts.length > 1 ? parts[1].trim() : parts[0].trim();

            // Find active hotels at this destination
            const hotelsAtDestination = allHotels.filter(hotel =>
                hotel.isActive !== false && (
                    hotel.location?.toLowerCase().includes(destination.toLowerCase()) ||
                    hotel.sub_destination?.toLowerCase().includes(destination.toLowerCase()) ||
                    hotel.destination?.toLowerCase().includes(destination.toLowerCase())
                )
            );

            // Set the first matching hotel for this day if available
            if (hotelsAtDestination.length > 0) {
                newHotelSelections[index] = {
                    location: destination,
                    hotelId: '', // Default to none
                    price: 0,
                    availableHotels: hotelsAtDestination
                };
            } else {
                // If no hotels found, still set the location
                newHotelSelections[index] = {
                    location: destination,
                    hotelId: '',
                    price: 0,
                    availableHotels: []
                };
            }
        }
    });

    return newHotelSelections;
};

export const mapFollowupToHotelSelections = (followupDetails, allHotels = []) => {
    if (!followupDetails || !Array.isArray(followupDetails)) return {};
    const selections = {};
    const grouped = {};

    // Group by journey_date if available and valid, else fallback to day or index
    followupDetails.forEach((item, idx) => {
        let dateKey = '';
        if (item.journey_date || item.date) {
            try {
                const parsedDate = new Date(item.journey_date || item.date);
                if (!isNaN(parsedDate.getTime())) {
                    dateKey = parsedDate.toISOString().split('T')[0];
                }
            } catch (e) {
                // Ignore date parsing error
            }
        }
        const groupKey = dateKey || (item.day !== undefined ? `day_${item.day}` : `idx_${idx}`);
        if (!grouped[groupKey]) grouped[groupKey] = [];
        grouped[groupKey].push(item);
    });

    Object.keys(grouped).forEach((dateKey, dayIndex) => {
        selections[dayIndex] = grouped[dateKey].map(item => {
            let hotelId = (typeof item.hotel_id === 'object' ? item.hotel_id?._id : item.hotel_id) || '';

            // If hotelId is empty or not in allHotels, try matching by hotel name
            if ((!hotelId || (allHotels && allHotels.length > 0 && !allHotels.some(h => h._id?.toString() === hotelId?.toString()))) && item.hotel_name) {
                const found = allHotels.find(h => h.hotel_name?.toLowerCase() === item.hotel_name?.toLowerCase());
                if (found) {
                    hotelId = found._id;
                }
            }

            return {
                location: item.destination || '',
                hotelId: hotelId ? hotelId.toString() : '',
                roomType: item.room_type || item.roomType || '',
                mealPlan: item.meal_plan || item.mealPlan || '',
                roomsCount: parseInt(item.roomsCount || item.rooms) || 1,
                extraCount: item.extraCount !== undefined ? parseInt(item.extraCount) : (item.extra_mattress !== undefined ? parseInt(item.extra_mattress) : undefined),
                availableHotels: [] // Will be populated by component if needed
            };
        });
    });
    return selections;
};

export const calculatePdfDimensions = (canvas, pdf) => {
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
    const imgX = (pdfWidth - imgWidth * ratio) / 2;
    const imgY = 0;

    return {
        imgX,
        imgY,
        imgWidth: imgWidth * ratio,
        imgHeight: imgHeight * ratio,
        ratio
    };
};

export const extractItineraryFromPackage = (packageDetails) => {
    if (!packageDetails?.shortItinerary || !Array.isArray(packageDetails.shortItinerary)) {
        return [];
    }

    return packageDetails.shortItinerary.map(day =>
        typeof day === 'string' ? day : day?.tagValue || day?.tagName || ''
    );
};

export const validateForm = (guestInfo, tripDetails, selectedPackage, isEditMode = false, validLocations = []) => {
    if (!guestInfo.guest_name?.trim()) {
        return { isValid: false, message: 'Please enter guest name', severity: 'error' };
    }

    const hasEmail = guestInfo.guest_email?.trim() && /\S+@\S+\.\S+/.test(guestInfo.guest_email);
    const hasPhone = guestInfo.guest_phone?.trim() && guestInfo.guest_phone.length >= 7;

    if (!hasEmail && !hasPhone) {
        return { isValid: false, message: 'Please enter a valid email address or phone number', severity: 'error' };
    }

    if (guestInfo.guest_email?.trim() && !/\S+@\S+\.\S+/.test(guestInfo.guest_email)) {
        return { isValid: false, message: 'Please enter a valid email address', severity: 'error' };
    }

    if (guestInfo.guest_phone?.trim() && guestInfo.guest_phone.length < 7) {
        return { isValid: false, message: 'Please enter a valid phone number', severity: 'error' };
    }

    // Location is mandatory
    if (!tripDetails.location || !tripDetails.location.trim()) {
        return { isValid: false, message: 'Location is required. Please select an approved location.', severity: 'error' };
    }

    // Check if inserted/selected location matches approved locations list
    if (Array.isArray(validLocations) && validLocations.length > 0) {
        const matchesApproved = validLocations.some(
            loc => (typeof loc === 'string' ? loc : loc.name)?.trim().toLowerCase() === tripDetails.location.trim().toLowerCase()
        );
        if (!matchesApproved) {
            return { isValid: false, message: `Only approved locations from Location master can be selected. Other text is not allowed.`, severity: 'error' };
        }
    }

    if (!tripDetails.pax || parseInt(tripDetails.pax) < 1) {
        return { isValid: false, message: 'Please enter valid number of guests', severity: 'error' };
    }

    if (!isEditMode && !selectedPackage) {
        return { isValid: false, message: 'Please select a package', severity: 'error' };
    }

    if (tripDetails.travel_date) {
        const travelDate = new Date(tripDetails.travel_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (travelDate < today) {
            return { isValid: false, message: 'Travel date cannot be in the past', severity: 'warning' };
        }
    }

    return { isValid: true };
};

export const deriveCarDetailsFromSelections = (carSelections, totalDays = null) => {
    if (!carSelections || typeof carSelections !== 'object') return [];

    // If totalDays is specified, only consider days 0 .. totalDays - 1
    const validDayIndices = (totalDays !== null && totalDays !== undefined && !isNaN(totalDays) && Number(totalDays) > 0)
        ? Array.from({ length: Number(totalDays) }, (_, i) => String(i))
        : Object.keys(carSelections).filter(k => !isNaN(k));

    return validDayIndices.reduce((acc, dayKey) => {
        const dayCars = carSelections[dayKey] || [];
        if (!Array.isArray(dayCars)) return acc;
        dayCars.forEach(c => {
            if (!c?.car_name || Number(c.car_count) <= 0) return;
            const count = Number(c.car_count);
            const existing = acc.find(a => a.car_name === c.car_name);
            if (existing) {
                existing.car_count = Math.max(existing.car_count, count);
            } else {
                acc.push({ car_name: c.car_name, car_count: count });
            }
        });
        return acc;
    }, []);
};

export const buildPayload = ({ guestInfo, tripDetails, selectedPackage, cost, stayInfo, hotelSelections, carSelections, allHotels, hotelSeason,
    carSeason,
    margin,
    itinerary,
    inclusions = [],
    exclusions = [],
    detailedItinerary = '',
    dayWiseDetailedItinerary = [],
    isDraft = false,
    isB2B = false
}) => {
    const gPhone = guestInfo.guest_phone || '';
    const gCountryCode = guestInfo.country_code || '+91';
    const cleanedPhone = gPhone.startsWith(gCountryCode)
        ? gPhone.replace(gCountryCode, '').trim()
        : gPhone;

    // Map selections to followup_details
    const followup_details = [];
    const duration = parseInt(tripDetails.duration) || 0;
    const startDate = tripDetails.travel_date ? new Date(tripDetails.travel_date) : null;

    const cleanHotelSelections = {};
    if (hotelSelections) {
        Object.keys(hotelSelections).forEach(key => {
            if (hotelSelections[key]) {
                const daySelections = Array.isArray(hotelSelections[key])
                    ? hotelSelections[key]
                    : [hotelSelections[key]];

                cleanHotelSelections[key] = daySelections.map(sel => {
                    if (sel) {
                        const { availableHotels, ...rest } = sel;
                        return rest;
                    }
                    return sel;
                });
            }
        });
    }

    for (let i = 0; i <= duration; i++) {
        let journeyDate = '';
        let checkinDate = '';
        let checkoutDate = '';

        if (startDate) {
            const currentDate = new Date(startDate);
            currentDate.setDate(startDate.getDate() + i);
            journeyDate = currentDate.toISOString();
            checkinDate = currentDate.toISOString();
            const nextDay = new Date(currentDate);
            nextDay.setDate(nextDay.getDate() + 1);
            checkoutDate = nextDay.toISOString();
        }

        const daySelections = Array.isArray(hotelSelections?.[i])
            ? hotelSelections[i]
            : (hotelSelections?.[i] ? [hotelSelections[i]] : [{}]);

        const totalAdults = parseInt(tripDetails?.pax) || 0;
        const totalKids = parseInt(tripDetails?.kids_above_5) || 0;
        const totalDayRooms = daySelections.reduce((sum, s) => sum + (parseInt(s?.roomsCount) || (parseInt(stayInfo?.rooms) > 0 ? parseInt(stayInfo.rooms) : 0) || 1), 0);

        const getRowAdults = (rowRooms) => totalDayRooms > 0 ? Math.round(totalAdults * rowRooms / totalDayRooms) : 0;
        const getRowKids = (rowRooms) => totalDayRooms > 0 ? Math.round(totalKids * rowRooms / totalDayRooms) : 0;

        daySelections.forEach((selection, sIdx) => {
            const carSelection = sIdx === 0 ? (carSelections?.[i] || []) : [];
            const hotel = allHotels?.find(h => h._id?.toString() === (selection?.hotelId?._id?.toString() || selection?.hotelId?.toString()));

            const rowRooms = parseInt(selection?.roomsCount) || (parseInt(stayInfo?.rooms) > 0 ? parseInt(stayInfo.rooms) : 0) || 1;
            const rowAdults = getRowAdults(rowRooms);
            const rowKids = getRowKids(rowRooms);

            const rowTripDetails = { ...tripDetails, pax: rowAdults, kids_above_5: rowKids };
            const specificStayInfo = { ...stayInfo, rooms: rowRooms };

            followup_details.push({
                journey_date: journeyDate,
                destination: selection?.location || '',
                hotel_id: (typeof selection?.hotelId === 'object' ? selection?.hotelId?._id : selection?.hotelId) || '',
                hotel_name: hotel?.hotel_name || '',
                hotel_amount: calculateSingleHotelCost(selection, allHotels, i, rowTripDetails, specificStayInfo) || 0,
                checkin_date: checkinDate,
                checkout_date: checkoutDate,
                room_type: selection?.roomType || '',
                meal_plan: selection?.mealPlan || '',
                rooms: rowRooms,
                roomsCount: rowRooms,
                extraCount: selection?.extraCount !== undefined && selection?.extraCount !== '' ? parseInt(selection.extraCount) : undefined,
                extra_mattress: selection?.extraCount !== undefined && selection?.extraCount !== '' ? parseInt(selection.extraCount) : undefined,
                vehicle_name: carSelection.filter(c => Number(c?.car_count) > 0).map(c => `${c.car_name} (${c.car_count})`).join(', '),
                approved_status: 'Pending',
                rejected_reason: []
            });
        });
    }

    const cleanCarSelections = {};
    for (let d = 0; d <= duration; d++) {
        if (carSelections?.[d] || carSelections?.[String(d)]) {
            cleanCarSelections[d] = carSelections[d] || carSelections[String(d)];
        }
    }

    const derivedCarDetails = deriveCarDetailsFromSelections(cleanCarSelections, duration + 1);
    const activeLegacyCars = tripDetails.car_details?.filter(car => Number(car.car_count) > 0) || [];
    const hasAnyDayCars = Object.values(cleanCarSelections || {}).some(list => (list || []).some(c => Number(c?.car_count) > 0));
    const effectiveCarDetails = hasAnyDayCars ? derivedCarDetails : activeLegacyCars;

    return {
        guest_info: {
            guest_name: guestInfo.guest_name,
            guest_email: guestInfo.guest_email,
            guest_phone: cleanedPhone,
            guest_address: guestInfo.guest_address || '',
            guest_country_code: gCountryCode,
        },
        pax: parseInt(tripDetails.pax),
        kids_above_5: parseInt(tripDetails.kids_above_5) || 0,
        car_details: effectiveCarDetails,
        cost: cost,
        destination: tripDetails.location || selectedPackage?.location || '',
        duration: parseInt(tripDetails.duration) || selectedPackage?.duration || 0,
        package_id: selectedPackage?._id === 'custom' ? null : selectedPackage?._id,
        stay_info: {
            rooms: parseInt(stayInfo.rooms) || 0,
            hotel: stayInfo.hotel,
            room_type: stayInfo.room_type || "",
        },
        followup_details, // New field for day-wise details
        itinerary: itinerary, // Store the short itinerary strings/objects
        hotel_selections: cleanHotelSelections, // Add raw hotel selections WITHOUT bloated availableHotels
        car_selections: cleanCarSelections, // Add raw car selections pruned to duration
        travel_date: tripDetails.travel_date || '',
        pickup_location: tripDetails.pickup_location || 'NJP / IXB',
        dropoff_location: tripDetails.dropoff_location || 'NJP / IXB',
        optional_extras: tripDetails.optional_extras || [],
        hotel_season: hotelSeason,
        car_season: carSeason,
        margin: margin,
        lead_source: guestInfo.lead_source || 'website',
        lead_stage: isDraft ? 'Draft' : 'New',
        important_notes: tripDetails.important_notes || '',
        inclusions: inclusions || [],
        exclusions: exclusions || [],
        detailed_itinerary: detailedItinerary || '',
        day_wise_detailed_itinerary: dayWiseDetailedItinerary || [],
        verified: !isDraft,
        is_b2b: isB2B,
    };
};

export const generatePdfFilename = (guestName, location) => {
    return `${guestName || 'inquiry'}_${location || 'package'}_${Date.now()}.pdf`;
};

export const validateDraftRequirements = (guestInfo) => {
    if (!guestInfo.guest_name || !guestInfo.guest_email) {
        return {
            isValid: false,
            message: 'Please enter at least guest name and email to save draft',
            severity: 'warning'
        };
    }
    return { isValid: true };
};

export const resetHotelSelections = (hotelSelections) => {
    const nextState = {};
    Object.keys(hotelSelections).forEach(key => {
        if (hotelSelections[key]) {
            const arr = Array.isArray(hotelSelections[key]) ? hotelSelections[key] : [hotelSelections[key]];
            nextState[key] = arr.map(item => ({
                ...item,
                hotelId: '',
                roomType: '',
                mealPlan: '',
                roomsCount: 1,
                extraCount: undefined
            }));
        }
    });
    return nextState;
};

export const updateItineraryItem = (currentItinerary, index, value) => {
    const newItinerary = [...currentItinerary];
    newItinerary[index] = value;
    return newItinerary;
};

export const updateHotelSelection = (hotelSelections, dayIndex, roomIndex, field, value) => {
    const daySelections = Array.isArray(hotelSelections[dayIndex])
        ? [...hotelSelections[dayIndex]]
        : (hotelSelections[dayIndex] ? [{ ...hotelSelections[dayIndex] }] : [{}]);

    if (daySelections[roomIndex]) {
        daySelections[roomIndex] = { ...daySelections[roomIndex], [field]: value };
    }

    return {
        ...hotelSelections,
        [dayIndex]: daySelections
    };
};

export const batchUpdateHotelSelection = (hotelSelections, dayIndex, roomIndex, fields) => {
    const daySelections = Array.isArray(hotelSelections[dayIndex])
        ? [...hotelSelections[dayIndex]]
        : (hotelSelections[dayIndex] ? [{ ...hotelSelections[dayIndex] }] : [{}]);

    if (daySelections[roomIndex]) {
        daySelections[roomIndex] = { ...daySelections[roomIndex], ...fields };
    }

    return { ...hotelSelections, [dayIndex]: daySelections };
};

export const addHotelRoomSelection = (hotelSelections, dayIndex) => {
    const daySelections = Array.isArray(hotelSelections[dayIndex])
        ? [...hotelSelections[dayIndex]]
        : (hotelSelections[dayIndex] ? [{ ...hotelSelections[dayIndex] }] : []);

    // Find the first complete row to use as a template
    const template = daySelections.find(s => s?.hotelId);

    const newRow = template
        ? {
            location: template.location || '',
            filterLocation: template.filterLocation || '',
            hotelId: template.hotelId,
            roomType: template.roomType || '',
            mealPlan: template.mealPlan || '',
            roomsCount: 1,
        }
        : {
            location: daySelections[0]?.location || '',
            filterLocation: daySelections[0]?.filterLocation || '',
            hotelId: '',
            roomType: '',
            mealPlan: '',
            roomsCount: 1,
        };

    daySelections.push(newRow);

    return { ...hotelSelections, [dayIndex]: daySelections };
};

export const removeHotelRoomSelection = (hotelSelections, dayIndex, roomIndex) => {
    let daySelections = Array.isArray(hotelSelections[dayIndex])
        ? [...hotelSelections[dayIndex]]
        : (hotelSelections[dayIndex] ? [{ ...hotelSelections[dayIndex] }] : []);

    if (daySelections.length > 1) {
        daySelections.splice(roomIndex, 1);
    }

    return {
        ...hotelSelections,
        [dayIndex]: daySelections
    };
};

export const addHotelExtraSelection = (hotelSelections, dayIndex, tripDetails = {}, stayInfo = {}) => {
    let daySelections = Array.isArray(hotelSelections[dayIndex])
        ? [...hotelSelections[dayIndex]]
        : (hotelSelections[dayIndex] ? [{ ...hotelSelections[dayIndex] }] : [{}]);

    if (daySelections.length === 0) daySelections = [{}];

    const targetIdx = daySelections.length - 1;
    const targetRow = { ...daySelections[targetIdx] };

    const totalAdults = parseInt(tripDetails?.pax) || 0;
    const totalDayRooms = daySelections.reduce((sum, s) => sum + (parseInt(s?.roomsCount) || (parseInt(stayInfo?.rooms) > 0 ? parseInt(stayInfo.rooms) : 1)), 0);
    const rowRooms = parseInt(targetRow?.roomsCount) || 1;
    const rowAdults = totalDayRooms > 0 ? Math.round(totalAdults * rowRooms / totalDayRooms) : 0;
    const defaultExtra = Math.max(0, rowAdults - rowRooms * 2);

    const currentExtra = (targetRow.extraCount !== undefined && targetRow.extraCount !== null && targetRow.extraCount !== '')
        ? parseInt(targetRow.extraCount)
        : defaultExtra;

    targetRow.extraCount = (currentExtra || 0) + 1;
    daySelections[targetIdx] = targetRow;

    return { ...hotelSelections, [dayIndex]: daySelections };
};

export const determineHotelSeason = (hotel, travelDate, dayIndex) => {
    if (!hotel || !hotel.season_dates || hotel.season_dates.length === 0) return 'off_season_price';
    if (!travelDate) return 'off_season_price';

    const startDate = new Date(travelDate);
    if (isNaN(startDate.getTime())) return 'off_season_price';

    const targetDate = new Date(startDate);
    targetDate.setDate(startDate.getDate() + (dayIndex || 0));
    const targetMMDD = targetDate.toISOString().substring(5, 10);

    const isSeason = hotel.season_dates.some(range => {
        const start = range.start;
        const end = range.end;
        if (!start || !end) return false;

        if (start <= end) {
            return targetMMDD >= start && targetMMDD <= end;
        } else {
            return targetMMDD >= start || targetMMDD <= end;
        }
    });

    return isSeason ? 'season_price' : 'off_season_price';
};

export const calculateSingleHotelCostWithBreakdown = (selection, allHotels, seasonOrDayIndex, tripDetails, stayInfo) => {
    const defaultBreakdown = {
        baseCost: 0,
        extraAdultCost: 0,
        childCost: 0,
        total: 0,
        details: {
            rooms: 0,
            basePrice: 0,
            extraAdults: 0,
            extraPrice: 0,
            kids: 0,
            childPrice: 0,
            isManualExtra: false
        }
    };

    if (!selection?.hotelId || !selection?.roomType || !selection?.mealPlan || !allHotels) return defaultBreakdown;

    const rooms = (parseInt(stayInfo?.rooms) > 0) ? parseInt(stayInfo.rooms) : 1;
    const adults = parseInt(tripDetails?.pax) || 0;
    const kids = parseInt(tripDetails?.kids_above_5) || 0;

    const selHotelId = (typeof selection.hotelId === 'object' ? selection.hotelId?._id : selection.hotelId)?.toString();
    const hotel = allHotels.find(h => h._id?.toString() === selHotelId);
    if (!hotel) return defaultBreakdown;

    let season = seasonOrDayIndex;
    if (typeof seasonOrDayIndex === 'number' || !isNaN(parseInt(seasonOrDayIndex))) {
        season = determineHotelSeason(hotel, tripDetails.travel_date, parseInt(seasonOrDayIndex));
    } else if (!season) {
        season = 'off_season_price';
    }

    const category = hotel.category?.find(cat => cat.room_cat === selection.roomType);
    if (!category) return defaultBreakdown;

    const pricing = category[season];
    if (!pricing) return defaultBreakdown;

    const planType = selection.mealPlan.split('_')[0];

    // 1. Base Room Cost
    const basePrice = pricing[selection.mealPlan] || 0;
    const baseCost = basePrice * rooms;

    // 2. Extra Mattress Cost
    const hasManualExtra = selection?.extraCount !== undefined && selection?.extraCount !== null && selection?.extraCount !== '';
    const extraAdults = hasManualExtra
        ? Math.max(0, parseInt(selection.extraCount) || 0)
        : (stayInfo?.extra_mattress !== undefined && stayInfo?.extra_mattress !== null && stayInfo?.extra_mattress !== ''
            ? Math.max(0, parseInt(stayInfo.extra_mattress) || 0)
            : Math.max(0, adults - (rooms * 2)));
    let extraAdultCost = 0;
    let extraPrice = 0;
    if (extraAdults > 0) {
        const extraMatKey = `extra_mat_${planType}`;
        extraPrice = pricing[extraMatKey] || 0;
        extraAdultCost = extraPrice * extraAdults;
    }

    // 3. Child No Bed (CNB) Cost
    let childCost = 0;
    let childPrice = 0;
    if (kids > 0) {
        const cnbKey = `cnb_${planType}`;
        childPrice = pricing[cnbKey] || 0;
        childCost = childPrice * kids;
    }

    return {
        baseCost,
        extraAdultCost,
        childCost,
        total: baseCost + extraAdultCost + childCost,
        details: {
            rooms,
            basePrice,
            extraAdults,
            extraPrice,
            kids,
            childPrice,
            isManualExtra: hasManualExtra
        }
    };
};

export const calculateSingleHotelCost = (selection, allHotels, season, tripDetails, stayInfo) => {
    return calculateSingleHotelCostWithBreakdown(selection, allHotels, season, tripDetails, stayInfo).total;
};

/**
 * Extra-mattress requirement, derived exactly the way the "Select Hotels Day-Wise"
 * section computes it: per night, room capacity = (rooms selected that night) x 2,
 * and any adults beyond that capacity each need an extra mattress.
 * Rooms come from each night's hotel rows (roomsCount, default 1) — NOT stayInfo.rooms.
 * Returns { perNight: [{ night, rooms, extra }], max, uniform, hasAny }.
 */
export const computeExtraMattress = (hotelSelections, tripDetails) => {
    const adults = parseInt(tripDetails?.pax) || 0;
    const nights = parseInt(tripDetails?.duration) || 0;
    const perNight = [];

    for (let d = 0; d < nights; d++) {
        let rows = Array.isArray(hotelSelections?.[d])
            ? hotelSelections[d]
            : (hotelSelections?.[d] ? [hotelSelections[d]] : [{}]);
        if (rows.length === 0) rows = [{}];

        const rooms = rows.reduce((sum, s) => sum + (parseInt(s?.roomsCount) || 1), 0);
        const hasManualRow = rows.some(s => s?.extraCount !== undefined && s?.extraCount !== null && s?.extraCount !== '');
        let extra = 0;
        if (hasManualRow) {
            extra = rows.reduce((sum, s) => {
                if (s?.extraCount !== undefined && s?.extraCount !== null && s?.extraCount !== '') {
                    return sum + Math.max(0, parseInt(s.extraCount) || 0);
                }
                const rowRooms = parseInt(s?.roomsCount) || 1;
                const rowAdults = rooms > 0 ? Math.round(adults * rowRooms / rooms) : 0;
                return sum + Math.max(0, rowAdults - rowRooms * 2);
            }, 0);
        } else {
            extra = Math.max(0, adults - rooms * 2);
        }
        perNight.push({ night: d + 1, rooms, extra });
    }

    const max = perNight.reduce((m, n) => Math.max(m, n.extra), 0);
    const uniform = perNight.every((n) => n.extra === max);
    return { perNight, max, uniform, hasAny: max > 0 };
};

export const hotelCostCalculation = (hotelSelections, allHotels, season, tripDetails, stayInfo) => {
    if (!hotelSelections) return 0;
    const duration = parseInt(tripDetails?.duration) || 0;
    let total = 0;

    // Only calculate for the number of nights (duration)
    for (let dayIndex = 0; dayIndex < duration; dayIndex++) {
        const daySelection = hotelSelections[dayIndex];
        if (daySelection) {
            const arr = Array.isArray(daySelection) ? daySelection : [daySelection];

            const totalAdults = parseInt(tripDetails?.pax) || 0;
            const totalKids = parseInt(tripDetails?.kids_above_5) || 0;
            const totalDayRooms = arr.reduce((sum, s) => sum + (parseInt(s?.roomsCount) || (parseInt(stayInfo?.rooms) > 0 ? parseInt(stayInfo.rooms) : 0) || 1), 0);

            const getRowAdults = (rowRooms) => totalDayRooms > 0 ? Math.round(totalAdults * rowRooms / totalDayRooms) : 0;
            const getRowKids = (rowRooms) => totalDayRooms > 0 ? Math.round(totalKids * rowRooms / totalDayRooms) : 0;

            const dayTotal = arr.reduce((acc, selection) => {
                const rowRooms = parseInt(selection?.roomsCount) || (parseInt(stayInfo?.rooms) > 0 ? parseInt(stayInfo.rooms) : 0) || 1;
                const rowAdults = getRowAdults(rowRooms);
                const rowKids = getRowKids(rowRooms);

                const rowTripDetails = { ...tripDetails, pax: rowAdults, kids_above_5: rowKids };
                const specificStayInfo = { ...stayInfo, rooms: rowRooms };

                return acc + calculateSingleHotelCost(selection, allHotels, dayIndex, rowTripDetails, specificStayInfo);
            }, 0);
            total += dayTotal;
        }
    }

    return total;
};

export const calculateCarCost = (configData, season, tripDetails, carSelections = null) => {
    const duration = parseInt(tripDetails?.duration) || 0;
    const totalDays = duration + 1;

    // If we have day-wise car selections, use them
    if (carSelections && Object.keys(carSelections).length > 0) {
        let total = 0;
        // Cars are calculated for total days (duration + 1)
        for (let dayIndex = 0; dayIndex < totalDays; dayIndex++) {
            const dayCars = carSelections[dayIndex];
            if (dayCars && Array.isArray(dayCars)) {
                const dayCost = dayCars.reduce((carAcc, carDetail) => {
                    const carConfig = configData?.additionalCosts?.car?.find(c => c.type === carDetail.car_name);
                    let unitPrice = carConfig?.cost?.[season] || 0;
                    return carAcc + (unitPrice * (carDetail.car_count || 0));
                }, 0);
                total += dayCost;
            }
        }
        return total;
    }

    // Fallback to legacy global car_details
    const carDetails = tripDetails?.car_details || [];
    const oneNightPerDayCarAmount = 4000;
    const twoNightPerDayCarAmount = 8500;
    const threeNightPerDayCarAmount = 13500;

    if (!carDetails || !configData) return 0;


    if (tripDetails.location === 'Sandakphu') {
        const carCount = carDetails[0]?.car_count || 1;
        return totalDays === 3 ? (twoNightPerDayCarAmount * carCount) :
            totalDays === 4 ? (threeNightPerDayCarAmount * carCount) : (oneNightPerDayCarAmount * carCount);
    }

    return carDetails.reduce((acc, carDetail) => {
        const carConfig = configData?.additionalCosts?.car?.find(c => c.type === carDetail.car_name);
        let unitPrice = carConfig?.cost?.[season] || 0;

        // (Removed Darjeeling hardcoded logic for Bolero and Innova, use 'Darjeeling Bolero'/'Darjeeling Innova' car types instead)

        return acc + (unitPrice * carDetail.car_count * totalDays);
    }, 0);
};

/**
 * Maps operation and query data to the format expected by exportQueryViewPDF.
 */
export const mapOperationToPdfData = (operationData, queriesData, fallbackGuestInfo = null) => {
    const followupSource = (operationData?.followup_details && operationData.followup_details.length > 0)
        ? operationData.followup_details
        : (queriesData?.followup_details || []);

    const itineraryData = followupSource.map((item, index) => ({
        id: item._id,
        day: (index + 1).toString(),
        date: item.journey_date || '',
        place: item.destination || queriesData?.itinerary?.[index] || queriesData?.destination || '',
        hotelName: item.hotel_name || '',
        hotelAmount: item.hotel_amount || '',
        hotelConfirmation: item.hotel_confirmation || '',
        checkinDate: item.checkin_date || '',
        checkoutDate: item.checkout_date || '',
        mealPlan: item.meal_plan || '',
        vehicleName: item.vehicle_name || '',
        vehiclePayment: item.vehicle_payment || '',
        vehicleStatus: item.vehicle_status || '',
        driverName: item.driver || item.driverName || '',
        hotelId: item.hotel_id || '',
        roomType: item.room_type || item.roomType || '',
        status: item.approved_status || '',
        rejected_reason: item.rejected_reason || [],
    })) || [];

    const effectiveCost = queriesData?.cost ?? operationData?.trip_details?.total_price ?? operationData?.trip_details?.price ?? operationData?.trip_details?.cost ?? '';
    const rawAdvance = queriesData?.advance ?? operationData?.trip_details?.advance_paid ?? (Array.isArray(operationData?.trip_details?.advance) ? operationData.trip_details.advance.reduce((a, b) => a + b, 0) : operationData?.trip_details?.advance) ?? '';
    const effectiveAdvance = rawAdvance !== '' ? rawAdvance : 0;

    const firstHotelFromItin = followupSource.find(f => f.hotel_name)?.hotel_name || '';
    const firstRoomTypeFromItin = followupSource.find(f => f.room_type)?.room_type || '';
    const firstVehicleFromItin = followupSource.find(f => f.vehicle_name)?.vehicle_name || '';

    // Extract car selections if available
    const activeCarSelections = queriesData?.car_selections
        ? Object.values(queriesData.car_selections).flat().filter(c => c?.car_name && Number(c.car_count) > 0)
        : [];
    const firstCarSelection = activeCarSelections[0] || queriesData?.car_selections?.['0']?.[0] || (queriesData?.car_selections && Object.values(queriesData.car_selections)?.[0]?.[0]);
    const carNameFromSelection = firstCarSelection?.car_name;
    const carCountFromSelection = firstCarSelection?.car_count;

    // Resolve assigned agent name
    let assignedUser = '';
    if (queriesData?.manage_teams && queriesData.manage_teams.length > 0) {
        const team = queriesData.manage_teams[0];
        assignedUser = team.user_id?.fullName || team.user_id?.name || '';
    }
    if (!assignedUser && operationData?.allocation && operationData.allocation.length > 0) {
        const alloc = operationData.allocation[0];
        assignedUser = alloc.user_id?.fullName || alloc.user_id?.name || '';
    }
    if (!assignedUser && queriesData?.confirmed_by) {
        assignedUser = queriesData.confirmed_by?.fullName || queriesData.confirmed_by?.name || '';
    }
    if (!assignedUser && queriesData?.creator && queriesData.creator[0]) {
        assignedUser = queriesData.creator[0]?.fullName || queriesData.creator[0]?.name || '';
    }

    const rawGuestName = queriesData?.guest_info?.guest_name || queriesData?.guest_id?.guest_name || queriesData?.guest_info?.name || queriesData?.guest_name || operationData?.guest_info?.name || operationData?.guest_id?.name || fallbackGuestInfo?.guest_name || fallbackGuestInfo?.name || '';

    const guestInfo = {
        name: typeof rawGuestName === 'string' ? rawGuestName.trim() : rawGuestName,
        country_code: queriesData?.guest_info?.guest_country_code || queriesData?.guest_id?.guest_country_code || operationData?.guest_info?.country_code || operationData?.guest_id?.country_code || fallbackGuestInfo?.guest_country_code || fallbackGuestInfo?.country_code || '+91',
        contact: queriesData?.guest_info?.guest_phone || queriesData?.guest_id?.guest_phone || queriesData?.guest_phone || operationData?.guest_info?.phone || operationData?.guest_id?.phone || queriesData?.guest_info?.contact || fallbackGuestInfo?.guest_phone || fallbackGuestInfo?.phone || fallbackGuestInfo?.contact || '',
        email: queriesData?.guest_info?.guest_email || queriesData?.guest_id?.guest_email || queriesData?.guest_email || operationData?.guest_info?.email || operationData?.guest_id?.email || fallbackGuestInfo?.guest_email || fallbackGuestInfo?.email || '',
        pax: operationData?.trip_details?.number_of_adults ?? operationData?.trip_details?.pax ?? queriesData?.pax ?? '',
        kids_above_5: operationData?.trip_details?.number_of_kids ?? operationData?.trip_details?.number_of_children ?? queriesData?.kids_above_5 ?? 0,
        hotel: queriesData?.stay_info?.hotel || operationData?.trip_details?.accommodation || firstHotelFromItin || '',
        roomType: queriesData?.stay_info?.room_type || queriesData?.stay_info?.roomType || operationData?.trip_details?.room_type || firstRoomTypeFromItin || '',
        rooms: queriesData?.stay_info?.rooms || operationData?.trip_details?.rooms || 1,
        carname: (activeCarSelections.length > 0 ? carNameFromSelection : null) || queriesData?.car_details?.[0]?.car_name || queriesData?.car_details?.car_name || carNameFromSelection || operationData?.trip_details?.vehicle || firstVehicleFromItin || '',
        carcount: (activeCarSelections.length > 0 ? carCountFromSelection : null) || queriesData?.car_details?.[0]?.car_count || queriesData?.car_details?.car_count || carCountFromSelection || operationData?.trip_details?.vehicle_count || 1,
        source: queriesData?.lead_source || queriesData?.guest_id?.lead_source || operationData?.lead_source || operationData?.trip_details?.source || '',
        bookingDate: queriesData?.created_at || queriesData?.createdAt || queriesData?.guest_id?.created_at || queriesData?.guest_info?.created_at || operationData?.createdAt || '',
        tourDate: queriesData?.travel_date?.slice(0, 10) || operationData?.trip_details?.travel_date?.slice(0, 10) || followupSource?.[0]?.journey_date?.slice(0, 10) || '',
        bookingStatus: queriesData?.lead_stage || operationData?.trip_status || '',
        cost: effectiveCost,
        advancePayment: effectiveAdvance,
        location: queriesData?.destination || queriesData?.guest_id?.destination || queriesData?.location || operationData?.trip_details?.destination || '',
        assigned_to: assignedUser,
        car_selections: queriesData?.car_selections || {},
        optional_extras: investigationExtras(queriesData, operationData),
        duePayment:
            !isNaN(parseFloat(effectiveCost)) && !isNaN(parseFloat(effectiveAdvance))
                ? Math.max(0, parseFloat(effectiveCost) - parseFloat(effectiveAdvance))
                : 0,
        important_notes: queriesData?.important_notes || '',
        quotation_no: queriesData?.quotation_no || queriesData?.quotationNo || queriesData?.quote_id || queriesData?.quoteNo || operationData?.quotation_no || fallbackGuestInfo?.quotation_no || '',
    };

    return { guestInfo, itineraryData };
};

/**
 * Maps operation and query data specifically for CreateInquiry editing.
 */
export const mapOperationToInquiry = (operationData, queriesData, packageData = null) => {
    if (!operationData || !queriesData) return null;

    const pkg = packageData || queriesData.package_id;
    const pkgDetails = pkg?.details || pkg;

    return {
        ...queriesData,
        _id: queriesData._id,
        operation_id: operationData._id,
        guest_id: queriesData.guest_id || operationData.guest_id || queriesData._id,
        guest_info: {
            guest_name: operationData.guest_info?.name || queriesData.guest_info?.guest_name,
            guest_email: operationData.guest_info?.email || queriesData.guest_info?.guest_email,
            guest_phone: operationData.guest_info?.phone || queriesData.guest_info?.guest_phone,
            guest_country_code: operationData.guest_info?.country_code || queriesData.guest_info?.guest_country_code || '+91',
        },
        pax: operationData.trip_details?.number_of_adults || queriesData.pax,
        kids_above_5: operationData.trip_details?.number_of_kids || queriesData.kids_above_5,
        destination: queriesData.destination || queriesData.location || pkg?.location || operationData.trip_details?.location || operationData.destination || '',
        duration: queriesData.duration || pkg?.duration,
        travel_date: queriesData.travel_date,
        cost: queriesData.cost ?? operationData.trip_details?.cost ?? operationData.cost ?? 0,
        // Prioritize: 1. Followup destinations (most accurate for confirmed), 2. Manual itinerary in query, 3. Package short itinerary
        itinerary: (operationData?.followup_details?.length > 1)
            ? operationData.followup_details.map(d => d.destination || '')
            : (queriesData?.itinerary?.length > 1)
                ? queriesData.itinerary
                : (pkgDetails?.shortItinerary && Array.isArray(pkgDetails.shortItinerary) && pkgDetails.shortItinerary.length > 0
                    ? extractItineraryFromPackage(pkgDetails)
                    : (queriesData?.itinerary?.length > 0
                        ? queriesData.itinerary
                        : (operationData?.followup_details?.length > 0
                            ? operationData.followup_details.map(d => d.destination || '')
                            : []
                        )
                    )
                ),
        followup_details: operationData?.followup_details || queriesData?.followup_details,
        hotel_selections: (queriesData?.hotel_selections && Object.keys(queriesData.hotel_selections).length > 0)
            ? queriesData.hotel_selections
            : (operationData?.hotel_selections && Object.keys(operationData.hotel_selections).length > 0 ? operationData.hotel_selections : null),
        car_selections: (queriesData?.car_selections && Object.keys(queriesData.car_selections).length > 0)
            ? queriesData.car_selections
            : (operationData?.car_selections || {}),
        stay_info: queriesData?.stay_info || operationData?.stay_info || {},
        hotel_season: operationData?.hotel_season || queriesData?.hotel_season || "off_season_price",
        car_season: operationData?.car_season || queriesData?.car_season || "off_season_price",
        margin: operationData?.margin ?? queriesData?.margin ?? 20,
        optional_extras: investigationExtras(queriesData, operationData),
    };
};

/**
 * Helper to find optional extras in possibly nested structures.
 * Merges and normalizes found extras.
 */
const investigationExtras = (q, o) => {
    if (!q && !o) return [];

    // Extract relevant query object if it's nested or an array
    const resolveQuery = (input) => {
        if (!input) return null;
        if (Array.isArray(input)) return input[0] || null;
        if (input.data && Array.isArray(input.data)) return input.data[0] || null;
        return input;
    };

    const query = resolveQuery(q);
    const operation = o || {};

    // Collect all potential sources of extras
    const sources = [
        query?.optional_extras,
        query?.details?.cost?.optionalExtras,
        operation?.optional_extras,
        operation?.trip_details?.optional_extras,
        // Add more legacy/variant fields if any
    ];

    const allRaw = [];
    sources.forEach(source => {
        if (Array.isArray(source)) {
            allRaw.push(...source);
        } else if (source && typeof source === 'object') {
            // In case it's a single object (rare but possible)
            allRaw.push(source);
        }
    });

    const uniqueMap = new Map();

    allRaw.forEach(item => {
        if (!item) return;
        let name = '';
        let price = 0;
        let count = 1;

        if (typeof item === 'string') {
            name = item.trim();
        } else if (typeof item === 'object') {
            name = (item.name || item.tagValue || item.tagName || item.label || '').trim();
            price = Number(item.price || 0);
            count = Number(item.count || 1);
        }

        if (name) {
            const key = name.toLowerCase();
            if (!uniqueMap.has(key)) {
                uniqueMap.set(key, { name, price, count });
            }
        }
    });

    return Array.from(uniqueMap.values());
};

/**
 * Generates and downloads/previews a PDF for the Single Query View.
 */
export const exportQueryViewPDF = async (
    {
        guestInfo,
        itineraryData,
    },
    { isPreview = false } = {},
    setLoading,
    showSnackbar
) => {
    const { default: jsPDF } = await import('jspdf');

    let logoDataUrl = null;
    try {
        const resp = await fetch('/easo.png');
        const blob = await resp.blob();
        logoDataUrl = await new Promise((res) => {
            const reader = new FileReader();
            reader.onload = () => res(reader.result);
            reader.readAsDataURL(blob);
        });
    } catch (_) { }

    setLoading(true);
    try {
        const pdf = new jsPDF('p', 'mm', 'a4');
        const W = pdf.internal.pageSize.getWidth();
        const H = pdf.internal.pageSize.getHeight();
        const marginL = 14;
        const marginR = W - 14;
        const contentW = marginR - marginL;
        let y = 0;

        const DARK_GREEN = [27, 94, 32];
        const WHITE = [255, 255, 255];
        const DARK = [31, 41, 55];
        const MID = [107, 114, 128];
        const LIGHT_BG = [245, 247, 250];

        const setC = (c) => pdf.setTextColor(...c);
        const fillC = (c) => pdf.setFillColor(...c);
        const drawC = (c) => pdf.setDrawColor(...c);

        const hRule = (color = [229, 231, 235]) => {
            drawC(color); pdf.setLineWidth(0.3);
            pdf.line(marginL, y, marginR, y);
            y += 3;
        };

        const addFooter = () => {
            const pg = pdf.internal.getNumberOfPages();
            pdf.setPage(pg);
            fillC(DARK_GREEN);
            pdf.rect(0, H - 18, W, 18, 'F');
            setC(WHITE); pdf.setFontSize(8);
            pdf.text('\u2295  www.easotrip.com', marginL + 10, H - 8);
            pdf.text('\u260E  +91 70017 24300', W / 2, H - 8, { align: 'center' });
            pdf.text('\u2709  easogroup@gmail.com', marginR - 10, H - 8, { align: 'right' });
            drawC(WHITE); pdf.setLineWidth(0.3);
            pdf.line(0, H - 18, W, H - 18);
        };

        const checkPage = (needed = 8) => {
            if (y + needed > H - 22) {
                addFooter();
                pdf.addPage();
                y = 14;
            }
        };

        const sectionTitle = (title) => {
            checkPage(14);
            fillC(DARK_GREEN);
            pdf.rect(marginL, y, contentW, 7, 'F');
            setC(WHITE); pdf.setFontSize(9); pdf.setFont(undefined, 'bold');
            pdf.text(title.toUpperCase(), marginL + 3, y + 5);
            y += 10;
            pdf.setFont(undefined, 'normal');
        };

        const twoCol = (lblL, valL, lblR, valR) => {
            checkPage(7);
            const colL = marginL;
            const colLV = marginL + 42;
            const colR = marginL + (contentW / 2) + 4;
            const colRV = colR + 38;

            setC(MID); pdf.setFontSize(8);
            pdf.text(lblL, colL, y);
            setC(DARK); pdf.setFont(undefined, 'bold'); pdf.setFontSize(8.5);
            pdf.text(String(valL || '—'), colLV, y);

            if (lblR) {
                setC(MID); pdf.setFontSize(8); pdf.setFont(undefined, 'normal');
                pdf.text(lblR, colR, y);
                setC(DARK); pdf.setFont(undefined, 'bold'); pdf.setFontSize(8.5);
                pdf.text(String(valR || '—'), colRV, y);
            }
            pdf.setFont(undefined, 'normal');
            y += 5.5;
        };

        // Header Background
        fillC(WHITE); pdf.rect(0, 0, W, 40, 'F');
        fillC(DARK_GREEN); pdf.circle(-18, -10, 38, 'F');
        drawC([220, 220, 220]); pdf.setLineWidth(0.4);
        pdf.line(0, 40, W, 40);

        if (logoDataUrl) {
            pdf.addImage(logoDataUrl, 'PNG', W - 54, 3, 44, 18);
        } else {
            setC(DARK_GREEN); pdf.setFontSize(16); pdf.setFont(undefined, 'bold');
            pdf.text('EASOTRIP', W - 14, 16, { align: 'right' });
        }

        if (guestInfo.quotation_no) {
            setC(DARK_GREEN); pdf.setFontSize(11); pdf.setFont(undefined, 'bold');
            pdf.text(`Quote ID: ${guestInfo.quotation_no}`, marginL, 34);
            pdf.setFont(undefined, 'normal');
        }

        y = 48;

        sectionTitle('Guest & Booking Information');
        if (guestInfo.quotation_no) {
            twoCol('Quote ID', guestInfo.quotation_no, 'Booking Status', guestInfo.bookingStatus);
            twoCol('Guest Name', guestInfo.name, 'Source', guestInfo.source);
        } else {
            twoCol('Guest Name', guestInfo.name, 'Booking Status', guestInfo.bookingStatus);
        }
        twoCol('Contact', `${guestInfo.country_code || '+91'} ${guestInfo.contact}`, 'Booking Date', guestInfo.bookingDate);
        twoCol('Email', guestInfo.email, 'Tour Date', guestInfo.tourDate);
        twoCol('Destination', guestInfo.location || guestInfo.package_location || '—', 'Assigned To', guestInfo.assigned_to);
        y += 4;

        sectionTitle('Trip & Stay Details');
        twoCol('No of Person', guestInfo.pax, 'Rooms', guestInfo.rooms);
        twoCol('Kids (5+)', guestInfo.kids_above_5 || 0, 'Room Type', guestInfo.roomType);
        twoCol('Car Name', guestInfo.carname, 'Car Count', guestInfo.carcount);
        y += 4;

        if (itineraryData && itineraryData.length > 0) {
            sectionTitle('Short Itinerary');
            itineraryData.forEach((item, idx) => {
                const label = `Day ${item.day}:  `;
                const labelWidth = pdf.getStringUnitWidth(label) * 8.5 / pdf.internal.scaleFactor;
                const place = item.place || '—';
                const hotel = item.hotelName ? `Hotel: ${item.hotelName}` : '';
                const vehicle = item.vehicleName ? ` | Vehicle: ${item.vehicleName}` : '';

                // Base itinerary content
                const itineraryContent = `${place}${hotel ? ' (' + hotel + ')' : ''}${vehicle}`;
                const bodyLines = pdf.splitTextToSize(itineraryContent, contentW - 10 - labelWidth);

                // Prepare car info if present in car_selections
                const dayCars = guestInfo.car_selections?.[idx] || [];
                const hasCars = dayCars.length > 0;
                let carLines = [];
                if (hasCars) {
                    const carStr = `Vehicles: ${dayCars.map(c => `${c.car_name} (${c.car_count})`).join(', ')}`;
                    carLines = pdf.splitTextToSize(carStr, contentW - 10 - labelWidth);
                }

                const totalTextLines = bodyLines.length + carLines.length;
                const rowH = Math.max(7, 5 * (totalTextLines || 1) + 2);
                checkPage(rowH + 2);

                if (idx % 2 === 0) {
                    fillC(LIGHT_BG);
                    pdf.rect(marginL, y - 1, contentW, rowH, 'F');
                }

                setC(DARK_GREEN); pdf.setFontSize(8.5); pdf.setFont(undefined, 'bold');
                pdf.text(label, marginL + 2, y + 4);

                setC(DARK); pdf.setFontSize(8); pdf.setFont(undefined, 'normal');
                if (bodyLines.length > 0) {
                    pdf.text(bodyLines[0], marginL + 2 + labelWidth, y + 4);
                    bodyLines.slice(1).forEach((line, li) => {
                        pdf.text(line, marginL + 2 + labelWidth, y + 4 + (li + 1) * 5);
                    });
                }

                if (hasCars) {
                    setC(DARK_GREEN); pdf.setFontSize(7.5); pdf.setFont(undefined, 'bold');
                    carLines.forEach((cLine, ci) => {
                        pdf.text(cLine, marginL + 2 + labelWidth, y + 4 + (bodyLines.length + ci) * 5);
                    });
                    pdf.setFont(undefined, 'normal');
                }

                y += rowH + 1;
            });
            y += 4;
        }

        if (guestInfo.important_notes && guestInfo.important_notes.trim() !== '') {
            sectionTitle('Important Notes');

            const noteLines = pdf.splitTextToSize(guestInfo.important_notes, contentW);
            noteLines.forEach(line => {
                checkPage(5);
                setC(DARK); pdf.setFontSize(8);
                pdf.text(line, marginL + 2, y);
                y += 5;
            });
            y += 4;
        }

        checkPage(20);
        fillC(DARK_GREEN);
        pdf.rect(marginL, y, contentW, 11, 'F');
        setC(WHITE); pdf.setFontSize(11); pdf.setFont(undefined, 'bold');
        pdf.text('TOTAL QUOTED PRICE : ', marginL + 4, y + 7.5);
        const costVal = Number(guestInfo.cost || 0);
        pdf.text(`Rs. ${costVal.toLocaleString('en-IN')}`, marginR - 35, y + 7.5, { align: 'right' });
        y += 16;

        const advance = Number(guestInfo.advancePayment || 0);
        const due = Number(guestInfo.duePayment || 0);
        twoCol('Advance Paid', `Rs. ${advance.toLocaleString('en-IN')}`, 'Amount Due', `Rs. ${due.toLocaleString('en-IN')}`);

        addFooter();

        const filename = `${guestInfo.name || 'query'}_quotation_${Date.now()}.pdf`;
        if (isPreview) {
            window.open(pdf.output('bloburl'), '_blank');
        } else {
            pdf.save(filename);
        }
        showSnackbar(isPreview ? 'PDF Preview opened' : 'PDF Downloaded successfully', 'success');
    } catch (error) {
        console.error('Error generating PDF:', error);
        showSnackbar('Failed to generate PDF', 'error');
    } finally {
        setLoading(false);
    }
};

export const findMatchingItineraryCard = (title, storedItineraries = [], allPackages = []) => {
    if (!title || typeof title !== 'string' || !title.trim()) return null;

    const cleanStr = (s) => String(s || '')
        .toLowerCase()
        .replace(/^(?:day\s*\d+\s*[:\-–]?\s*|\d+[\.\)]\s*)/i, '')
        .replace(/[^a-z0-9]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const target = cleanStr(title);
    if (!target) return null;

    // 1. Exact match in storedItineraries
    for (const card of (storedItineraries || [])) {
        if (!card) continue;
        const cardTitle = cleanStr(card.title || '');
        if (cardTitle === target) {
            return {
                title: card.title,
                description: card.description || '',
                destination: card.destination || '',
                id: card._id,
                source: 'storedItinerary'
            };
        }
    }

    // 2. Contains match in storedItineraries
    for (const card of (storedItineraries || [])) {
        if (!card) continue;
        const cardTitle = cleanStr(card.title || '');
        if (cardTitle && (cardTitle.includes(target) || target.includes(cardTitle))) {
            return {
                title: card.title,
                description: card.description || '',
                destination: card.destination || '',
                id: card._id,
                source: 'storedItinerary'
            };
        }
    }

    // 3. Fallback: Check package detailed itineraries
    if (allPackages && Array.isArray(allPackages)) {
        for (const pkg of allPackages) {
            const days = pkg.details?.itinerary;
            if (Array.isArray(days)) {
                for (const d of days) {
                    const hVal = cleanStr(d.headerValue || d.headerTag || '');
                    if (hVal && (hVal === target || hVal.includes(target) || target.includes(hVal))) {
                        const paraHtml = Array.isArray(d.para) ? d.para.join('') : (d.para || '');
                        if (paraHtml) {
                            return {
                                title: d.headerValue || d.headerTag || title,
                                description: paraHtml,
                                destination: pkg.location || '',
                                id: pkg._id,
                                source: 'package'
                            };
                        }
                    }
                }
            }
        }
    }

    return null;
};

export const isTitleInDb = (title, storedItineraries = []) => {
    if (!title || typeof title !== 'string' || !title.trim()) return false;
    const cleanStr = (s) =>
        String(s || '')
            .toLowerCase()
            .replace(/^(?:day\s*\d+\s*[:\-–]?\s*|\d+[\.\)]\s*)/i, '')
            .replace(/[^a-z0-9]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();

    const target = cleanStr(title);
    if (!target) return false;

    return (storedItineraries || []).some((card) => {
        if (!card?.title) return false;
        const cardTitle = cleanStr(card.title);
        return cardTitle === target;
    });
};

export const resolveApprovedLocation = (rawLocation, locationsList = []) => {
    if (!rawLocation) return '';
    const raw = String(rawLocation).trim();
    if (!raw) return '';

    const list = (locationsList || []).map(l => (typeof l === 'string' ? l : l.name)).filter(Boolean);

    // 1. Exact match (case-insensitive)
    const exact = list.find(l => l.trim().toLowerCase() === raw.toLowerCase());
    if (exact) return exact;

    // 2. Normalized match (without non-alphanumeric chars)
    const cleanRaw = raw.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (cleanRaw) {
        const cleanMatch = list.find(l => l.toLowerCase().replace(/[^a-z0-9]/g, '') === cleanRaw);
        if (cleanMatch) return cleanMatch;
    }

    // 3. Substring match: check if a master location name is contained in raw, or raw is contained in master location
    // e.g. "Kalimpong And Gangtok Package" contains "Gangtok" / "Kalimpong"
    // e.g. "Gangtok Tour" contains "Gangtok"
    // Sort by longest master location name first so e.g. "North Sikkim" matches before "Sikkim"
    const sortedList = [...list].sort((a, b) => b.length - a.length);
    const subMatch = sortedList.find(l => {
        const lNorm = l.toLowerCase();
        return raw.toLowerCase().includes(lNorm) || lNorm.includes(raw.toLowerCase());
    });
    if (subMatch) return subMatch;

    // 4. Return trimmed raw location as fallback (never blank out if rawLocation exists!)
    return raw;
};

